import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Строка статуса почтового ящика Exchange по UPN. Если интеграция выключена
 * (502 от API), компонент молча прячется. В рабочем состоянии показывает:
 *   • «Ящик есть» + primarySmtpAddress
 *   • либо «Ящика нет» + кнопка «Создать»
 *
 * Создание зовёт `POST /api/exchange/mailbox` — это запускает Enable-Mailbox
 * на стороне Windows-агента.
 */
export default function ExchangeMailboxRow({ upn }) {
  const can = useCan();
  const canRead  = can('exchange.manage', 'read');
  const canWrite = can('exchange.manage', 'write');

  const [state, setState]   = useState('idle'); // idle|loading|ok|missing|hidden|error
  const [info, setInfo]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!canRead || !upn) { setState('idle'); return; }
    let cancelled = false;
    setState('loading'); setError(null);
    api.get('/api/exchange/mailbox?upn=' + encodeURIComponent(upn))
      .then((res) => {
        if (cancelled) return;
        if (res?.exists) { setInfo(res); setState('ok'); }
        else             { setInfo(null); setState('missing'); }
      })
      .catch((e) => {
        if (cancelled) return;
        // 502 ex_error на отключённой интеграции — прячем строку, чтобы не пугать
        const msg = e instanceof ApiError ? (e.message || '') : '';
        if (msg.includes('выключена')) { setState('hidden'); return; }
        setError(msg || 'Не удалось проверить ящик');
        setState('error');
      });
    return () => { cancelled = true; };
  }, [upn, canRead]);

  const onCreate = async () => {
    if (!canWrite || !upn) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/exchange/mailbox', { upn });
      setInfo(res);
      setState('ok');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось создать ящик');
    } finally {
      setBusy(false);
    }
  };

  if (!canRead || !upn || state === 'hidden') return null;

  return (
    <div className="ex-mb-row">
      <Icon name="mail" size={13} />
      {state === 'loading' && <span className="ex-mb-row__hint">Проверяю почтовый ящик…</span>}
      {state === 'ok' && info && (
        <span className="ex-mb-row__ok">
          Ящик есть: <span className="mono">{info.primarySmtpAddress}</span>
          {info.database ? <span className="ex-mb-row__hint"> · {info.database}</span> : null}
        </span>
      )}
      {state === 'missing' && (
        <>
          <span className="ex-mb-row__hint">Почтового ящика нет в Exchange</span>
          {canWrite && (
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={onCreate}
              disabled={busy}
              style={{ marginLeft: 'auto' }}
            >
              {busy ? 'Создаю…' : 'Создать ящик'}
            </button>
          )}
        </>
      )}
      {state === 'error' && (
        <span className="ex-mb-row__err">Ошибка: {error}</span>
      )}
    </div>
  );
}
